import React, {
  useCallback, useState, useRef, ReactElement, RefObject,
} from 'react';
import {
  useSwipeable, SwipeEventData, SwipeableHandlers, DOWN,
} from 'react-swipeable';
import ShadowBlock from '../shadow-block/shadow-block.component';
import RefreshIcon from '../../icons/refresh-icon';
import MainService from '../../services/main-service';
import FormatService from '../../services/format-service';
import useMobileDevice from '../../hooks/use-mobile-device';
import useStylesPullToRefresh from './pull-to-refresh.styles';

const PullToRefresh: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const SWIPE_RELOAD_DISTANCE_THRESHOLD: number = 250;
  const SWIPE_RELOAD_START_THRESHOLD: number = 70;
  const SWIPE_VIEW_THRESHOLD: number = 100;
  const SWIPE_DIRECTION_DOWN: string = DOWN;
  const SWIPE_START_DEFAULT: number = 0;
  const REFRESH_TIMEOUT: number = 3000;
  const HEADER_HEIGHT: number = 68;
  const HEADER_OFFSET: number = 1;

  const {
    isDisabled,
    onRefresh,
    children,
  } = props;

  const { classes } = useStylesPullToRefresh();
  const isMobileDevice: boolean = useMobileDevice();
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);

  const isSwipeDisabled: boolean = isDisabled || (!isMobileDevice && !MainService.IS_MOBILE_APP);

  const [swipeStartY, setSwipeStartY] = useState(SWIPE_START_DEFAULT);
  const [swipeCurrentY, setSwipeCurrentY] = useState(SWIPE_START_DEFAULT);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadingClassName: string | undefined = isRefreshing ? classes.pullToRefreshLoading : undefined;

  const onSwipeStart: Function = useCallback((eventData: SwipeEventData): void => {
    const containerScreenPosition: DOMRect | null = containerRef.current
      ? containerRef.current.getBoundingClientRect() : null;
    const containerTopOffset: number | null = containerScreenPosition ? containerScreenPosition.top : null;

    if (!isSwipeDisabled && eventData.dir === SWIPE_DIRECTION_DOWN && containerTopOffset
      && containerTopOffset >= HEADER_HEIGHT + HEADER_OFFSET) {
      const initialCoordinateY: number | null = (eventData.event
        && (eventData.event as TouchEvent).touches[0].clientY) || null;

      if (initialCoordinateY && initialCoordinateY > SWIPE_RELOAD_START_THRESHOLD) {
        setSwipeStartY(eventData.absY);
      }
    }
  }, [isSwipeDisabled]);

  const onSwipedDown: Function = useCallback((eventData: SwipeEventData): void => {
    if (!isSwipeDisabled) {
      if (eventData.dir === SWIPE_DIRECTION_DOWN && swipeStartY > SWIPE_START_DEFAULT) {
        const swipeEndY: number = eventData.absY;
        const swipeDistance: number = swipeEndY - swipeStartY;

        if (swipeDistance > SWIPE_RELOAD_DISTANCE_THRESHOLD) {
          onRefresh();

          setIsRefreshing(true);

          setTimeout((): void => {
            setSwipeCurrentY(SWIPE_START_DEFAULT);
            setSwipeStartY(SWIPE_START_DEFAULT);
            setIsRefreshing(false);
          }, REFRESH_TIMEOUT);

          return;
        }
      }

      setSwipeCurrentY(SWIPE_START_DEFAULT);
      setSwipeStartY(SWIPE_START_DEFAULT);
      setIsRefreshing(false);
    }
  }, [isSwipeDisabled, swipeStartY]);

  const onSwiping: Function = useCallback((eventData: SwipeEventData): void => {
    if (!isSwipeDisabled && eventData.dir === SWIPE_DIRECTION_DOWN && swipeStartY > SWIPE_START_DEFAULT) {
      const currentY: number = eventData.absY;

      if (currentY - swipeStartY > SWIPE_VIEW_THRESHOLD) {
        setSwipeCurrentY(currentY);
      }
    }
  }, [isSwipeDisabled, swipeStartY]);

  const swipeableHandlers: SwipeableHandlers = useSwipeable({
    onSwipeStart: (eventData: SwipeEventData): void => onSwipeStart(eventData),
    onSwipedDown: (eventData: SwipeEventData): void => onSwipedDown(eventData),
    onSwiping: (eventData: SwipeEventData): void => onSwiping(eventData),
  });

  return (
    <div ref={containerRef}>
      <div>
        <ShadowBlock
          className={FormatService.formatClassNames(classes.pullToRefresh, loadingClassName)}
          style={{
            top: swipeCurrentY || SWIPE_START_DEFAULT,
            display: swipeCurrentY > SWIPE_VIEW_THRESHOLD ? 'block' : 'none',
          }}
        >
          <RefreshIcon className={classes.iconRefresh} />
        </ShadowBlock>
      </div>
      <div {...swipeableHandlers}>
        {children}
      </div>
    </div>
  );
});

export default PullToRefresh;
